import React, {} from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Settings, Plus, Archive, Download, Upload, FileJson, FileSpreadsheet, FileText, RefreshCw, BarChart3, Activity, Clock, CheckCircle, History, ChevronDown, Percent } from 'lucide-react';
import BackButton from '../../components/BackButton';
import ActifsTab from './ActifsTab';
import HistoriqueTab from './HistoriqueTab';
import AjustementsTab from './AjustementsTab';
import Modals from './Modals';
import useParametresBaticoLogic from './useParametresBaticoLogic';
import useParametresBaticoExtra from './useParametresBaticoExtra';

const ParametresBATICO = () => {
  const data = useParametresBaticoLogic();
  const {
    ajustementDialogOpen, ajustementForm, ajustementsMontants, customParamDialogOpen, customParamForm, customParams,
    dialogOpen, editMode, editingAjustement, editingCustomParam, editingRestriction, expandedSections,
    exportToCSV, exportToExcel, exportToJSON, fetchAjustementsMontants, fetchCustomParams, fetchData,
    fetchTerritoires, fileInputRef, formData, handleArchive, handleCreateAjustement, handleDelete,
    handleDeleteAjustement, handleEdit, handleInputChange, handleSubmit, handleUpdateAjustement, historique,
    importData, importDialogOpen, loading, openEditAjustement, openNewDialog, parametresActifs,
    previewDialogOpen, previewParams, resetAjustementForm, restrictionDialogOpen, restrictionForm, restrictions,
    selectedParams, setAjustementDialogOpen, setAjustementForm, setAjustementsMontants, setCustomParamDialogOpen, setCustomParamForm,
    setCustomParams, setDialogOpen, setEditMode, setEditingAjustement, setEditingCustomParam, setEditingRestriction,
    setExpandedSections, setFormData, setHistorique, setImportData, setImportDialogOpen, setLoading,
    setParametresActifs, setPreviewDialogOpen, setPreviewParams, setRestrictionDialogOpen, setRestrictionForm, setRestrictions,
    setSelectedParams, setStats, setTerritoiresDisponibles, stats, territoiresDisponibles, toggleSection} = data;
  const {
    confirmImport, copyToClipboard, fetchRestrictions, formatCustomParamValue, getParamTypeInfo, getRestrictionActionInfo,
    getRestrictionTypeInfo, handleDeleteCustomParam, handleDeleteRestriction, handleFileImport, handlePreview, handleSaveCustomParam,
    handleSaveRestriction, handleToggleCustomParam, handleToggleRestriction, openCustomParamDialog, openRestrictionDialog, toggleApplicableA} = useParametresBaticoExtra(data);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Bouton retour */}
        <BackButton label="Retour" className="mb-2" />
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                <Settings className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Paramètres BATICO
                </h1>
                <p className="text-slate-600 text-sm lg:text-base">
                  Configuration des coefficients RCD Solidaire et gestion des remises
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Import/Export Dropdown */}
              <div className="relative group">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Exporter
                  <ChevronDown className="h-3 w-3" />
                </Button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 hidden group-hover:block">
                  <button
                    onClick={exportToJSON}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm"
                  >
                    <FileJson className="h-4 w-4 text-amber-600" />
                    Export JSON
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm"
                  >
                    <FileText className="h-4 w-4 text-green-600" />
                    Export CSV
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2 text-sm"
                  >
                    <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                    Export Excel
                  </button>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4" />
                Importer
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileImport}
                accept=".json,.csv"
                className="hidden"
              />

              <Button 
                variant="outline" 
                onClick={fetchData}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Actualiser
              </Button>

              <Button 
                onClick={openNewDialog}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveaux Paramètres
              </Button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Paramètres</p>
                  <p className="text-3xl font-bold">{stats.totalParams}</p>
                </div>
                <BarChart3 className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Actifs</p>
                  <p className="text-3xl font-bold">{stats.activeParams}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-slate-500 to-slate-600 text-white border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm">Archivés</p>
                  <p className="text-3xl font-bold">{stats.archivedParams}</p>
                </div>
                <Archive className="h-10 w-10 text-slate-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Remise Moyenne</p>
                  <p className="text-3xl font-bold">{(stats.avgRemise * 100).toFixed(1)}%</p>
                </div>
                <Percent className="h-10 w-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Dernière MAJ</p>
                  <p className="text-lg font-bold">
                    {stats.lastUpdate 
                      ? new Date(stats.lastUpdate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
                      : 'N/A'}
                  </p>
                </div>
                <Clock className="h-10 w-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="actifs" className="w-full">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl shadow-sm">
            <TabsTrigger 
              value="actifs" 
              className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white px-6"
            >
              <Activity className="h-4 w-4 mr-2" />
              Paramètres Actifs
            </TabsTrigger>
            <TabsTrigger 
              value="historique"
              className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white px-6"
            >
              <History className="h-4 w-4 mr-2" />
              Historique
              <Badge className="ml-2 bg-slate-700 text-white font-semibold">{historique.length}</Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="ajustements"
              className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white px-6"
            >
              <Settings className="h-4 w-4 mr-2" />
              Ajustements Montants
              <Badge className="ml-2 bg-purple-700 text-white font-semibold">{ajustementsMontants.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="actifs" className="mt-6">
            <ActifsTab
              parametresActifs={parametresActifs}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
              setExpandedSections={setExpandedSections}
              customParams={customParams}
              restrictions={restrictions}
              handlePreview={handlePreview}
              handleEdit={handleEdit}
              handleArchive={handleArchive}
              openCustomParamDialog={openCustomParamDialog}
              handleToggleCustomParam={handleToggleCustomParam}
              handleDeleteCustomParam={handleDeleteCustomParam}
              formatCustomParamValue={formatCustomParamValue}
              getParamTypeInfo={getParamTypeInfo}
              openRestrictionDialog={openRestrictionDialog}
              handleToggleRestriction={handleToggleRestriction}
              handleDeleteRestriction={handleDeleteRestriction}
              getRestrictionTypeInfo={getRestrictionTypeInfo}
              getRestrictionActionInfo={getRestrictionActionInfo}
              copyToClipboard={copyToClipboard}
              openNewDialog={openNewDialog}
            />
          </TabsContent>

          <TabsContent value="historique" className="mt-6">
            <HistoriqueTab
              historique={historique}
              handlePreview={handlePreview}
              handleEdit={handleEdit}
              handleArchive={handleArchive}
              handleDelete={handleDelete}
            />
          </TabsContent>

          <TabsContent value="ajustements" className="mt-6">
            <AjustementsTab
              ajustementsMontants={ajustementsMontants}
              setEditingAjustement={setEditingAjustement}
              resetAjustementForm={resetAjustementForm}
              setAjustementDialogOpen={setAjustementDialogOpen}
              openEditAjustement={openEditAjustement}
              handleDeleteAjustement={handleDeleteAjustement}
            />
          </TabsContent>
        </Tabs>

        <input ref={fileInputRef} type="file" accept=".json,.csv" onChange={handleFileImport} className="hidden" />

        <Modals
          dialogOpen={dialogOpen} setDialogOpen={setDialogOpen}
          editMode={editMode} setEditMode={setEditMode}
          formData={formData} setFormData={setFormData}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          importDialogOpen={importDialogOpen} setImportDialogOpen={setImportDialogOpen}
          importData={importData} setImportData={setImportData}
          confirmImport={confirmImport}
          fileInputRef={fileInputRef}
          handleFileImport={handleFileImport}
          previewDialogOpen={previewDialogOpen} setPreviewDialogOpen={setPreviewDialogOpen}
          previewParams={previewParams}
          customParamDialogOpen={customParamDialogOpen} setCustomParamDialogOpen={setCustomParamDialogOpen}
          editingCustomParam={editingCustomParam}
          customParamForm={customParamForm} setCustomParamForm={setCustomParamForm}
          handleSaveCustomParam={handleSaveCustomParam}
          toggleApplicableA={toggleApplicableA}
          restrictionDialogOpen={restrictionDialogOpen} setRestrictionDialogOpen={setRestrictionDialogOpen}
          editingRestriction={editingRestriction}
          restrictionForm={restrictionForm} setRestrictionForm={setRestrictionForm}
          handleSaveRestriction={handleSaveRestriction}
          territoiresDisponibles={territoiresDisponibles}
          ajustementDialogOpen={ajustementDialogOpen} setAjustementDialogOpen={setAjustementDialogOpen}
          editingAjustement={editingAjustement}
          ajustementForm={ajustementForm} setAjustementForm={setAjustementForm}
          handleCreateAjustement={handleCreateAjustement}
          handleUpdateAjustement={handleUpdateAjustement}
          handleEdit={handleEdit}
        />
    </div>
  );
};

export default ParametresBATICO;
